// SPDX-License-Identifier: GPL-3.0-or-later
// The live check of the game page: ask GET /api/live now and every 60 s, but only while the page is
// visible. A hidden page asks nothing. Any fault (network, status, bad body) means "not live"
// (fail closed). The module keeps no state on disk. The caller starts and stops it: the page stops
// it when the source is "off", so an "off" page makes no request.
// The game page uses this module. The /overlay page must never import it.

import { LIVE_POLL_MS, LIVE_ROUTE } from "./stream-config.ts";

/** The part of `fetch` that the check uses. A reply needs `ok` and `json`. */
export type LiveFetch = (
  input: string,
  init?: RequestInit,
) => Promise<{ readonly ok: boolean; json(): Promise<unknown> }>;

/** The part of a document that tells if the page is visible. */
export interface VisibilitySource {
  readonly hidden: boolean;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

/** What the check takes from the platform. Tests pass plain objects. */
export interface LiveStatusDeps {
  /** The fetch function, or null when there is none (the check never runs). */
  readonly fetchFn: LiveFetch | null;
  /** A clock in ms. */
  readonly now: () => number;
  /** Start a timer. The result is the handle for `clearTimer`. */
  readonly setTimer: (fn: () => void, ms: number) => unknown;
  readonly clearTimer: (handle: unknown) => void;
  /** The page visibility, or null (the page then counts as visible). */
  readonly visibility: VisibilitySource | null;
  /** Called when the answer changes. */
  readonly onChange: (live: boolean) => void;
  /** The time between two checks. Default: 60 s. */
  readonly intervalMs?: number;
}

/** The check that the page keeps. */
export interface LiveStatus {
  /** Start the checks: one now (when the page is visible), then one per interval. A second call does nothing. */
  start(): void;
  /** Stop the checks. The answer goes back to "not live" and a check in flight is ignored. */
  stop(): void;
  /** The last answer. */
  live(): boolean;
}

/** Ask the route once. It never throws. */
async function askOnce(fetchFn: LiveFetch): Promise<boolean> {
  try {
    const reply = await fetchFn(LIVE_ROUTE, { method: "GET", credentials: "omit" });
    if (!reply.ok) return false;
    const body = (await reply.json()) as { live?: unknown } | null;
    return body?.live === true;
  } catch {
    return false;
  }
}

/** The state of one check. The helpers below read and write it. */
interface LiveState {
  readonly deps: LiveStatusDeps;
  readonly interval: number;
  running: boolean;
  busy: boolean;
  live: boolean;
  timer: unknown;
  lastAt: number;
  epoch: number;
  /** The timer callback. One function for the life of the check. */
  poll: () => void;
  onVisibility: () => void;
}

function liveStatusHidden(s: LiveState): boolean {
  return s.deps.visibility?.hidden === true;
}

function liveStatusCancel(s: LiveState): void {
  if (s.timer === null) return;
  s.deps.clearTimer(s.timer);
  s.timer = null;
}

function liveStatusSchedule(s: LiveState, ms: number): void {
  liveStatusCancel(s);
  if (s.running && !liveStatusHidden(s)) s.timer = s.deps.setTimer(s.poll, ms);
}

function liveStatusFinish(s: LiveState, mine: number, answer: boolean): void {
  if (mine !== s.epoch) return;
  s.busy = false;
  if (answer !== s.live) {
    s.live = answer;
    s.deps.onChange(answer);
  }
  liveStatusSchedule(s, s.interval);
}

function liveStatusPoll(s: LiveState): void {
  s.timer = null;
  const { fetchFn } = s.deps;
  if (!s.running || s.busy || liveStatusHidden(s) || fetchFn === null) return;
  s.busy = true;
  s.lastAt = s.deps.now();
  const mine = s.epoch;
  void askOnce(fetchFn).then((answer) => liveStatusFinish(s, mine, answer));
}

// A page that comes back waits for the rest of the interval, so a quick tab switch makes no new call.
function liveStatusOnVisibility(s: LiveState): void {
  if (!s.running) return;
  if (liveStatusHidden(s)) {
    liveStatusCancel(s);
    return;
  }
  const wait = s.lastAt + s.interval - s.deps.now();
  if (wait <= 0) liveStatusPoll(s);
  else liveStatusSchedule(s, wait);
}

function liveStatusStart(s: LiveState): void {
  if (s.running || s.deps.fetchFn === null) return;
  s.running = true;
  s.deps.visibility?.addEventListener("visibilitychange", s.onVisibility);
  liveStatusPoll(s);
}

function liveStatusStop(s: LiveState): void {
  if (!s.running) return;
  s.running = false;
  s.busy = false;
  s.epoch += 1;
  s.live = false;
  liveStatusCancel(s);
  s.deps.visibility?.removeEventListener("visibilitychange", s.onVisibility);
}

/** Make the check. It does nothing until `start`. */
export function createLiveStatus(deps: LiveStatusDeps): LiveStatus {
  const s: LiveState = {
    deps,
    interval: deps.intervalMs ?? LIVE_POLL_MS,
    running: false,
    busy: false,
    live: false,
    timer: null,
    lastAt: Number.NEGATIVE_INFINITY,
    epoch: 0,
    poll: () => liveStatusPoll(s),
    onVisibility: () => liveStatusOnVisibility(s),
  };
  return {
    start: () => liveStatusStart(s),
    stop: () => liveStatusStop(s),
    live: () => s.live,
  };
}
